import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { IReducer } from '../../../../../../redux/configureStore';
import Card from '../../../../../../shared/components/Card';
import {
  CardLabel,
  CardValue,
  ContentCard,
  LabelAndValueContainer,
} from '../../../../../../shared/components/Card/components/CardComponents';
import theme from '../../../../../../styles/theme';
import checkValue from '../../../../../../utils/values';
import CardWithTable from '../CardWithTable';
import BarChart from '../components/CardWithBarChart/components/BarChart';
import Frame from './components/Frame';
import PdfTedPartnersPassedOn from './components/PdfTedPartnersPassedOn';

function TedDashboard() {
  const profilePartnerState = useSelector(
    (reducer: IReducer) => reducer.partnerProfile
  );
  const faresFilterState = useSelector(
    (reducer: IReducer) => reducer.faresFilter
  );
  const tedFaresState = useSelector((reducer: IReducer) => reducer.tedFares);
  const globalIsActive = Object.prototype.hasOwnProperty.call(
    tedFaresState.income.value,
    'Fiducia'
  );

  const hasData = useMemo(() => {
    if (
      tedFaresState.partnerPassedOn.length > 0 &&
      profilePartnerState.nivel === 2
    ) {
      return true;
    }
    return false;
  }, [tedFaresState.partnerPassedOn, profilePartnerState.nivel]);

  function isPartnerGlobalBarChart() {
    if (faresFilterState.searched.partner === 'global') {
      return true;
    }
    return false;
  }

  return (
    <Frame.Base
      isActive={tedFaresState.isActive}
      globalIsActive={globalIsActive}>
      <Card
        isActive
        title="TEDs Recebidas"
        display="flex"
        gridArea="tedsReceived"
        justifyContent="space-around"
        flexDirection="row"
        height="100%"
        width="100%"
        backgroundColor="#FFFFFF">
        <LabelAndValueContainer alignCenter>
          <CardLabel>Total</CardLabel>
          <CardValue color={theme.colors.emphasis}>
            {checkValue(tedFaresState.tedsReceived.value).toLocaleString(
              'pt-br',
              {
                style: 'currency',
                currency: 'BRL',
              }
            )}
          </CardValue>
        </LabelAndValueContainer>
        <LabelAndValueContainer alignCenter>
          <CardLabel>Quantidade</CardLabel>
          <CardValue color={theme.colors.emphasis}>
            {tedFaresState.tedsReceived.quantity}
          </CardValue>
        </LabelAndValueContainer>
      </Card>
      <Card
        isActive
        title="TEDs Enviadas"
        display="flex"
        gridArea="tedsSent"
        justifyContent="space-around"
        flexDirection="row"
        height="100%"
        width="100%"
        backgroundColor="#FFFFFF">
        <LabelAndValueContainer alignCenter>
          <CardLabel>Total</CardLabel>
          <CardValue color={theme.colors.emphasis}>
            {checkValue(tedFaresState.tedsSent.value).toLocaleString('pt-br', {
              style: 'currency',
              currency: 'BRL',
            })}
          </CardValue>
        </LabelAndValueContainer>
        <LabelAndValueContainer alignCenter>
          <CardLabel>Quantidade</CardLabel>
          <CardValue color={theme.colors.emphasis}>
            {tedFaresState.tedsSent.quantity}
          </CardValue>
        </LabelAndValueContainer>
      </Card>
      <Card
        isActive={!globalIsActive}
        title="Tarifas Recebidas"
        display="flex"
        gridArea="faresReceived"
        justifyContent="center"
        flexDirection="row"
        height="100%"
        width="100%"
        backgroundColor="#FFFFFF">
        <LabelAndValueContainer alignCenter>
          <CardLabel>Total</CardLabel>
          <CardValue color={theme.colors.emphasis}>
            {checkValue(tedFaresState.faresReceived.value).toLocaleString(
              'pt-br',
              {
                style: 'currency',
                currency: 'BRL',
              }
            )}
          </CardValue>
          <CardLabel>Quantidade</CardLabel>
          <CardValue color={theme.colors.emphasis}>
            {tedFaresState.faresReceived.quantity}
          </CardValue>
        </LabelAndValueContainer>
      </Card>

      <Card
        isActive={globalIsActive}
        title="Tarifas Recebidas"
        display="flex"
        gridArea="faresReceived"
        justifyContent="space-around"
        flexDirection="row"
        height="100%"
        width="100%"
        backgroundColor="#FFFFFF">
        <LabelAndValueContainer alignCenter>
          <CardLabel>Global</CardLabel>
          <CardValue color={theme.colors.emphasis}>
            {checkValue(tedFaresState.fares.global).toLocaleString('pt-br', {
              style: 'currency',
              currency: 'BRL',
            })}
          </CardValue>
        </LabelAndValueContainer>
        <LabelAndValueContainer alignCenter>
          <CardLabel>Fiducia</CardLabel>
          <CardValue color={theme.colors.emphasis}>
            {checkValue(tedFaresState.fares.fiducia).toLocaleString('pt-br', {
              style: 'currency',
              currency: 'BRL',
            })}
          </CardValue>
        </LabelAndValueContainer>
        <LabelAndValueContainer alignCenter>
          <CardLabel>Parceiros</CardLabel>
          <CardValue color={theme.colors.emphasis}>
            {checkValue(tedFaresState.fares.partner).toLocaleString('pt-br', {
              style: 'currency',
              currency: 'BRL',
            })}
          </CardValue>
        </LabelAndValueContainer>
      </Card>

      <Card
        isActive={!globalIsActive}
        title="Tarifas Repassadas"
        display="flex"
        gridArea="faresPassedOn"
        justifyContent="center"
        flexDirection="row"
        height="100%"
        width="100%"
        backgroundColor="#FFFFFF">
        <LabelAndValueContainer alignCenter>
          <CardLabel>Total</CardLabel>
          <CardValue color={theme.colors.red}>
            {checkValue(tedFaresState.faresPassedOn.value).toLocaleString(
              'pt-br',
              {
                style: 'currency',
                currency: 'BRL',
              }
            )}
          </CardValue>
          <CardLabel>Quantidade</CardLabel>
          <CardValue color={theme.colors.red}>
            {tedFaresState.faresPassedOn.quantity}
          </CardValue>
        </LabelAndValueContainer>
      </Card>

      <Card
        isActive={globalIsActive}
        title="Resultados"
        display="flex"
        gridArea="income"
        justifyContent="space-around"
        flexDirection="row"
        height="100%"
        width="100%"
        backgroundColor="#FFFFFF">
        <LabelAndValueContainer alignCenter>
          <CardLabel>Global</CardLabel>
          <CardValue color={theme.colors.emphasis}>
            {checkValue(
              tedFaresState.income.value.ambiente_382 > 0
                ? tedFaresState.income.value.ambiente_382
                : 0
            ).toLocaleString('pt-br', {
              style: 'currency',
              currency: 'BRL',
            })}
          </CardValue>
        </LabelAndValueContainer>
        <LabelAndValueContainer alignCenter>
          <CardLabel>Fiducia</CardLabel>
          <CardValue color={theme.colors.emphasis}>
            {checkValue(
              tedFaresState.income.value.Fiducia > 0
                ? tedFaresState.income.value.Fiducia
                : 0
            ).toLocaleString('pt-br', {
              style: 'currency',
              currency: 'BRL',
            })}
          </CardValue>
        </LabelAndValueContainer>
        <LabelAndValueContainer alignCenter>
          <CardLabel>Parceiros</CardLabel>
          <CardValue
            color={
              tedFaresState.income.value.Parceiro >= 0
                ? theme.colors.emphasis
                : theme.colors.red
            }>
            {checkValue(
              tedFaresState.income.value.Parceiro > 0
                ? tedFaresState.income.value.Parceiro
                : 0
            ).toLocaleString('pt-br', {
              style: 'currency',
              currency: 'BRL',
            })}
          </CardValue>
        </LabelAndValueContainer>
      </Card>

      <Card
        isActive={!globalIsActive}
        gridArea="income"
        title="Resultado"
        display="flex"
        flexDirection="column"
        justifyContent="flex-start"
        width="100%"
        height="100%"
        backgroundColor="#FFFFFF">
        <ContentCard hasOneData alignCenter justifyCenter>
          <CardLabel>Total</CardLabel>
          <CardValue hasOneData color={theme.colors.emphasis}>
            {checkValue(
              tedFaresState.income.value > 0 ? tedFaresState.income.value : 0
            ).toLocaleString('pt-br', {
              style: 'currency',
              currency: 'BRL',
            })}
          </CardValue>
        </ContentCard>
      </Card>

      <CardWithTable
        isActive={hasData}
        gridArea="table"
        title="Repasses de Tarifas"
        subtitle=""
        valueColor="#18A3A8"
        data={tedFaresState.partnerPassedOn}
        pdf={{
          title: 'Tarifas de TEDs Repassadas para Parceiros',
          body: <PdfTedPartnersPassedOn />,
        }}
      />

      <Card
        isActive={!isPartnerGlobalBarChart()}
        title="Tarifas Movimentadas nos Últimos 12 Meses"
        gridArea="lastTwelveMonths"
        display="flex"
        flexDirection="column"
        justifyContent="flex-start"
        width="100%"
        height="100%"
        backgroundColor="#FFFFFF">
        <BarChart
          data={tedFaresState.lastTwelveMonths}
          hasOneData={false}
          firstValueColor={theme.colors.emphasis}
          secondValueColor={theme.colors.red}
          labelForTwoDatas={{
            firstLabel: `Tarifas recebidas   `,
            secondLabel: 'Tarifas repassadas',
          }}
        />
      </Card>

      <Card
        isActive={isPartnerGlobalBarChart()}
        title="Tarifas Movimentadas nos Últimos 12 Meses"
        gridArea="lastTwelveMonths"
        display="flex"
        flexDirection="column"
        justifyContent="flex-start"
        width="100%"
        height="100%"
        backgroundColor="#FFFFFF">
        <BarChart
          data={tedFaresState.lastTwelveMonths}
          hasOneData={false}
          firstValueColor={theme.colors.emphasis}
          secondValueColor={theme.colors.blue}
          labelForTwoDatas={{
            firstLabel: `Tarifas recebidas (Fidúcia)   `,
            secondLabel: 'Tarifas repassadas (Parceiros)',
          }}
        />
      </Card>
    </Frame.Base>
  );
}

export default TedDashboard;
